/**
 * WareBee Digital Twin - KPI Color Utilities
 * Provides functions for consistent color usage from themeWareBeeDark
 */

import { TwTheme } from '../../../Tw';
import { getBestContrastColor } from '../../common/color.helper';

const colors = TwTheme.extend.colors;
/**
 * Maps KPI status to appropriate theme colors
 */
export const getKpiStatusColor = (
  status: 'critical' | 'warning' | 'good' | 'exceptional' | string,
): string => {
  // Use heatmap colors for proper theme consistency
  switch (status.toLowerCase()) {
    case 'critical':
      return colors.heatmap[90]; // Critical - red
    case 'warning':
      return colors.heatmap[50]; // Warning - yellow/orange
    case 'good':
      return colors.heatmap[20]; // Good - green
    case 'exceptional':
      return colors.heatmap[10]; // Exceptional - bright green
    default:
      return colors.chart.empty; // Default/empty
  }
};

/**
 * Maps KPI status to appropriate text colors for contrast
 */
export const getKpiStatusTextColor = (
  status: 'critical' | 'warning' | 'good' | 'exceptional' | string,
): string => {
  switch (status.toLowerCase()) {
    case 'critical':
      return colors.heatmapText[90]; // White for contrast on red
    case 'warning':
      return colors.heatmapText[50]; // Black for contrast on yellow
    case 'good':
      return colors.heatmapText[20]; // Black for contrast on green
    case 'exceptional':
      return colors.heatmapText[10]; // Black for contrast on bright green
    default:
      return colors.text.light; // Default light text
  }
};

/**
 * Maps process types to appropriate theme colors
 */
// Helper function to get the correct process type color from theme
export const getProcessTypeColor = (processType: string): [string, string] => {
  const processColor =
    colors.ActivityEventProcessType[processType] ||
    colors.ActivityEventProcessType.OTHER;
  return [processColor, getBestContrastColor(processColor)];
};

// export const getProcessTypeColor = (processType: string): string => {
//   const processColors = colors.eventGroups;

//   switch (processType.toLowerCase()) {
//     case 'picking':
//       return processColors.picking;
//     case 'packing':
//       return processColors.packing;
//     case 'receiving':
//       return processColors.handling;
//     case 'putaway':
//       return processColors.handling;
//     case 'returns_processing':
//       return processColors.reordering;
//     case 'crossdocking':
//       return processColors.travellingEndpoint;
//     case 'shipping':
//       return processColors.travellingHorizontal;
//     case 'maintenance':
//       return processColors.maintenance;
//     default:
//       return processColors.total;
//   }
// };

/**
 * Maps KPI categories to appropriate theme colors
 */
export const getCategoryColor = (category: string): string => {
  switch (category) {
    case 'warehouse_summary':
      return colors.brand.DEFAULT;
    case 'today':
      return colors.brand.accent;
    case 'process':
      return colors.events.handlingExecution;
    case 'inventory':
      return colors.bay.shelf;
    case 'labour':
      return colors.eventGroups.labourTotal;
    case 'clients':
      return colors.dimensionsColors[1];
    case 'insights':
      return colors.brand.chat;
    case 'issues':
      return colors.alerts.issue;
    case 'compliance':
      return colors.compliance.compliant;
    case 'projects':
      return colors.timeline.selectedBorder;
    case 'heatmaps':
      return colors.heatmap[50];
    case 'data_io':
      return colors.brand.cctv;
    default:
      return colors.app.DEFAULT;
  }
};

/**
 * Get theme-aligned colors for threshold ranges
 */
export const getThresholdRangeColors = (
  label: string,
): { color: string; textColor: string } => {
  switch (label.toLowerCase()) {
    case 'critical':
      return {
        color: colors.heatmap[90],
        textColor: colors.heatmapText[90],
      };
    case 'warning':
    case 'moderate risk':
      return {
        color: colors.heatmap[50],
        textColor: colors.heatmapText[50],
      };
    case 'good':
    case 'low risk':
      return {
        color: colors.heatmap[20],
        textColor: colors.heatmapText[20],
      };
    case 'exceptional':
    case 'high risk':
      return {
        color: colors.heatmap[10],
        textColor: colors.heatmapText[10],
      };
    default:
      return {
        color: colors.chart.empty,
        textColor: colors.text.light,
      };
  }
};

/**
 * Get colors for SCOR model levels
 */
export const getScorLevelColor = (level: string): string => {
  switch (level.toLowerCase()) {
    case 'advantage':
      return colors.heatmap[10];
    case 'parity':
      return colors.heatmap[50];
    case 'disadvantage':
      return colors.heatmap[90];
    default:
      return colors.chart.empty;
  }
};

export default {
  getKpiStatusColor,
  getKpiStatusTextColor,
  getProcessTypeColor,
  getCategoryColor,
  getThresholdRangeColors,
  getScorLevelColor,
};
